import { useState } from "react";
import { Check, ImageOff } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { imgUrl } from "@/lib/api";
import { toggleOption, useOptionDefs } from "@/lib/options";

const Thumb = ({ src, onZoom, testid }) =>
  src ? (
    <span role="button" tabIndex={0} data-testid={testid} onClick={(e) => { e.stopPropagation(); onZoom(src); }}
      className="w-16 h-16 shrink-0 border border-[#3B4157] img-stage overflow-hidden hover:border-orange-500 transition-colors cursor-zoom-in">
      <img src={imgUrl(src)} alt="" className="w-full h-full object-contain p-1" loading="lazy" />
    </span>
  ) : (
    <span className="w-16 h-16 shrink-0 border border-[#3B4157] grid place-items-center text-slate-600"><ImageOff className="w-5 h-5" /></span>
  );

export function OptionsPicker({ options, selected, setSelected }) {
  const { t, lang } = useLang();
  const [defs] = useOptionDefs();
  const [zoom, setZoom] = useState(null);
  return (
    <div className="mt-10" data-testid="product-options">
      <h2 className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500">{t("options_title")}</h2>
      <div className="mt-3 border border-[#3B4157] divide-y divide-[#3B4157]">
        {options.map((o) => {
          const on = selected.includes(o.key);
          const d = defs[o.key] || {};
          const name = (lang !== "lt" ? d.name_en : d.name_lt) || t(`opt_${o.key}`);
          const desc = lang !== "lt" ? d.desc_en || d.desc_lt : d.desc_lt;
          return (
            <div key={o.key} role="button" tabIndex={0} data-testid={`option-${o.key}-checkbox`} onClick={() => setSelected(toggleOption(selected, o.key))}
              className={`w-full flex items-center gap-3 px-3 py-3 text-sm text-left cursor-pointer transition-colors ${on ? "bg-orange-500/10" : "hover:bg-[#30354A]"}`}>
              <span className={`w-5 h-5 grid place-items-center border shrink-0 ${on ? "bg-orange-500 border-orange-500 text-[#1F2230]" : "border-slate-600"}`}>
                {on && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
              </span>
              <Thumb src={d.image} onZoom={setZoom} testid={`option-${o.key}-image`} />
              <span className="flex-1 min-w-0">
                <span className="block font-medium">{name}</span>
                {desc && <span data-testid={`option-${o.key}-desc`} className="block text-xs text-slate-400 mt-0.5 leading-snug">{desc}</span>}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 mt-2">{t("options_hint")}</p>
      {zoom && (
        <button data-testid="option-image-zoom" onClick={() => setZoom(null)} className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-6">
          <span className="img-stage p-10 max-w-3xl w-full aspect-square grid place-items-center"><img src={imgUrl(zoom)} alt="" className="max-w-full max-h-full object-contain" /></span>
        </button>
      )}
    </div>
  );
}
